import { FC, PropsWithChildren, useMemo } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  MaterialDesignContent,
  SnackbarOrigin,
  SnackbarProvider as SP,
  SnackbarProviderProps,
} from 'notistack';

import { useInjection } from '@hooks';
import { LocalStorageStore } from '@stores';

import { DI_TOKENS } from '../../constants';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(({ theme }) => ({
  '&.notistack-MuiContent': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontFamily: 'Readex Pro',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
    borderRadius: '1rem',
    padding: '1rem 1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    minWidth: '320px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid',
    borderColor: theme.palette.divider,

    '& #notistack-snackbar': {
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '0.75rem',
    },

    '& svg': {
      fontSize: '1.25rem',
      flexShrink: 0,
    },
  },

  '&.notistack-MuiContent-success': {
    borderLeftWidth: '4px',
    borderLeftColor: theme.palette.success.main,
    '& svg': {
      color: theme.palette.success.main,
    },
  },

  '&.notistack-MuiContent-error': {
    borderLeftWidth: '4px',
    borderLeftColor: theme.palette.error.main,
    '& svg': {
      color: theme.palette.error.main,
    },
  },

  '&.notistack-MuiContent-warning': {
    borderLeftWidth: '4px',
    borderLeftColor: theme.palette.warning.main,
    '& svg': {
      color: theme.palette.warning.main,
    },
  },

  '&.notistack-MuiContent-info': {
    borderLeftWidth: '4px',
    borderLeftColor: theme.palette.info.main,
    '& svg': {
      color: theme.palette.info.main,
    },
  },

  '&.notistack-MuiContent-default': {
    borderLeftWidth: '4px',
    borderLeftColor: theme.palette.primary.main,
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
}));

const snackbarConfig: SnackbarProviderProps = {
  autoHideDuration: 4000,
  iconVariant: {
    success: <CheckCircleRoundedIcon />,
    error: <ErrorRoundedIcon />,
    warning: <WarningRoundedIcon />,
    info: <InfoRoundedIcon />,
  },
  Components: {
    success: StyledMaterialDesignContent,
    error: StyledMaterialDesignContent,
    warning: StyledMaterialDesignContent,
    info: StyledMaterialDesignContent,
    default: StyledMaterialDesignContent,
  },
};

export const SnackbarProvider: FC<PropsWithChildren> = observer(({ children }) => {
  const localStorageStore = useInjection<LocalStorageStore>(DI_TOKENS.LocalStorageStore);
  const direction = localStorageStore.direction;

  const anchorOrigin = useMemo(
    () => ({
      vertical: 'top',
      horizontal: direction === 'ltr' ? 'right' : 'left',
    }),
    [direction],
  ) as SnackbarOrigin;

  return (
    <SP {...snackbarConfig} anchorOrigin={anchorOrigin} SnackbarProps={{ dir: direction }}>
      {children}
    </SP>
  );
});
